import axios from "axios"
import { TodayAPIClient } from "../common"

export type PreferType =
  | "order"
  | "package-item"
  | "parallel"
  | "order_with_temporary_order"
export class Invoice extends TodayAPIClient {
  constructor(baseUrl: string, token: string) {
    const axiosInstance = axios.create({
      baseURL: baseUrl,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })

    axiosInstance.interceptors.response.use(
      (v) => v,
      (error) => {
        if (error.config && error.response?.status >= 500) {
          return new Promise((resolve) => {
            setTimeout(() => {
              resolve(axiosInstance.request(error.config))
            }, 5000)
          })
        }
        return Promise.reject(error)
      }
    )
    super(axiosInstance)
  }

  async retrieveInvoiceImage({
    clientId,
    orderId,
    version,
    pageNumber,
    totalPageCount,
    rotate,
    preferType = "order",
    hideSenderAddress = false,
  }: {
    clientId: string
    orderId: string
    version: number
    pageNumber?: number
    totalPageCount?: number
    rotate?: boolean
    preferType?: PreferType
    hideSenderAddress?: boolean
  }): Promise<string> {
    const queryParams = new URLSearchParams()
    if (pageNumber) {
      queryParams.append("page_number", pageNumber.toString())
    }
    if (totalPageCount) {
      queryParams.append("total_page_count", totalPageCount.toString())
    }
    if (rotate) {
      queryParams.append("rotate", rotate.toString())
    }
    queryParams.append("prefer_type", preferType)
    if (hideSenderAddress) {
      queryParams.append("hide_sender_address", "true")
    }

    const response = await this.client.get(
      `/invoice/v${version}/clients/${clientId}/orders/${orderId}/invoice?${queryParams}`,
      {
        responseType: "arraybuffer",
      }
    )
    const image = Buffer.from(
      new Uint8Array(response.data).reduce(
        (data, byte) => data + String.fromCharCode(byte),
        ""
      ),
      "binary"
    ).toString("base64")
    return `data:${response.headers[
      "content-type"
    ].toLowerCase()};base64,${image}`
  }

  async retrieveInvoicePdf({
    clientId,
    orderIds,
    preferType = "order",
    multipage = true,
    hideSenderAddress = false,
  }: {
    clientId: string
    orderIds: string[]
    preferType?: PreferType
    multipage?: boolean
    hideSenderAddress?: boolean
  }): Promise<string> {
    const queryParams = new URLSearchParams({
      order_id: orderIds.join(","),
      prefer_type: preferType,
      multipage: multipage.toString(),
    })
    if (hideSenderAddress) {
      queryParams.append("hide_sender_address", "true")
    }

    const response = await this.client.get(
      `/invoice/v2/clients/${clientId}/orders/invoices?${queryParams}`,
      {
        responseType: "arraybuffer",
      }
    )
    const pdf = Buffer.from(
      new Uint8Array(response.data).reduce(
        (data, byte) => data + String.fromCharCode(byte),
        ""
      ),
      "binary"
    ).toString("base64")
    return `data:${response.headers[
      "content-type"
    ].toLowerCase()};base64,${pdf}`
  }

  async retrieveInvoicePdfInternal(params: {
    orderIds: string[]
    totalPageCount?: number
    pageOffset?: number
    preferType?: PreferType
  }): Promise<string> {
    const pdfByte = new Uint8Array(
      await this.retrieveInvoicePdfByteInternal(params)
    )
    const pdfBase64 = Buffer.from(
      pdfByte.reduce((data, byte) => data + String.fromCharCode(byte), ""),
      "binary"
    ).toString("base64")
    return `data:application/pdf;base64,${pdfBase64}`
  }

  async retrieveInvoicePdfByteInternal({
    orderIds,
    totalPageCount,
    pageOffset,
    duplicateProductCount,
    preferType = "order",
    multipage = true,
    width = 200,
    hideSenderAddress = false,
  }: {
    orderIds: string[]
    totalPageCount?: number
    pageOffset?: number
    duplicateProductCount?: boolean
    preferType?: PreferType
    multipage?: boolean
    // 서버(normalizeWidth)는 120 이상 200 이하의 임의 값을 허용한다.
    width?: number
    hideSenderAddress?: boolean
  }): Promise<ArrayBufferLike> {
    const queryParams = new URLSearchParams({
      order_id: orderIds.join(","),
      prefer_type: preferType,
      multipage: multipage.toString(),
    })
    if (totalPageCount) {
      queryParams.append("total_page_count", `${totalPageCount}`)
    }
    if (pageOffset) {
      queryParams.append("page_offset", `${pageOffset}`)
    }
    if (duplicateProductCount) {
      queryParams.append("duplicate_product_count", "true")
    }
    if (width) {
      queryParams.append("width", `${width}`)
    }

    if (hideSenderAddress) {
      queryParams.append("hide_sender_address", "true")
    }
    const response = await this.client.get(
      `/invoice/v2/orders/invoices?${queryParams}`,
      {
        responseType: "arraybuffer",
      }
    )
    return response.data
  }

  /**
   * 단건 조회 엔드포인트로 단일 주문의 송장 PDF(bytes)를 받아온다.
   *
   * 벌크(다건) 엔드포인트(`/orders/invoices`)가 아닌, 단건 엔드포인트
   * (`/clients/:clientId/orders/:orderId/invoice`)를 호출한다.
   * PDF 로 응답받으려면 multipage=true 가 필요하다(false 면 image/jpeg 반환).
   */
  async retrieveInvoicePdfByteSingle({
    clientId,
    orderId,
    version = 2,
    preferType = "order",
    multipage = true,
    width = 200,
    hideSenderAddress = false,
  }: {
    clientId: string
    orderId: string
    version?: number
    preferType?: PreferType
    multipage?: boolean
    // 서버(normalizeWidth)는 120 이상 200 이하의 임의 값을 허용한다.
    width?: number
    hideSenderAddress?: boolean
  }): Promise<ArrayBufferLike> {
    const queryParams = new URLSearchParams({
      prefer_type: preferType,
      multipage: multipage.toString(),
    })
    if (width) {
      queryParams.append("width", `${width}`)
    }
    if (hideSenderAddress) {
      queryParams.append("hide_sender_address", "true")
    }
    const response = await this.client.get(
      `/invoice/v${version}/clients/${clientId}/orders/${orderId}/invoice?${queryParams}`,
      {
        responseType: "arraybuffer",
      }
    )
    return response.data
  }
}
